<script setup>
/* eslint-disable @intlify/vue-i18n/no-dynamic-keys */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import WorkflowCanvasCard from './WorkflowCanvasCard.vue';
import WorkflowFlowConnector from './WorkflowFlowConnector.vue';
import WorkflowStepTree from './WorkflowStepTree.vue';
import {
  buildWorkflowAddStepMenuItems,
  getWorkflowTriggerStepVisual,
} from './workflowCanvasStepTheme';
import { toDateTimeWindowForm } from './dateTimeWindow';

const props = defineProps({
  automation: { type: Object, required: true },
  automationActionTypes: { type: Array, required: true },
  automationRuleEvents: { type: Array, required: true },
  isReadOnly: { type: Boolean, default: false },
});

const emit = defineEmits(['insertActionAfter']);

const activePanel = defineModel('activePanel', {
  type: String,
  required: true,
});
const activeAddMenuId = ref(null);

const { t } = useI18n();

const currentEventLabel = computed(() => {
  const ev = props.automationRuleEvents.find(
    event => event.key === props.automation.event_name
  );
  return ev?.value || props.automation.event_name;
});

const triggerVisual = computed(() => getWorkflowTriggerStepVisual());

const ENABLED_WORKFLOW_ACTION_KEYS = new Set([
  'assign_agent',
  'assign_ai_agent',
  'assign_team',
  'send_message',
  'add_private_note',
  'update_contact_tag',
  'date_time_window',
]);

const addStepMenuItems = computed(() =>
  buildWorkflowAddStepMenuItems(t).map(item => ({
    ...item,
    disabled: !ENABLED_WORKFLOW_ACTION_KEYS.has(item.key),
  }))
);

const branchTargetIds = computed(() =>
  (props.automation.actions || []).flatMap(action => {
    if (action.action_name !== 'date_time_window') {
      return [];
    }

    const rawParams = Array.isArray(action.action_params)
      ? action.action_params[0]
      : action.action_params;
    const params = rawParams?.start_date
      ? rawParams
      : toDateTimeWindowForm(rawParams);

    return [params.success_next_step_id, params.failure_next_step_id].filter(
      Boolean
    );
  })
);

const rootStepId = computed(
  () =>
    props.automation.actions?.[0]?.id && String(props.automation.actions[0].id)
);

const triggerActive = computed(
  () => activePanel.value === 'trigger-conditions'
);

const select = panel => {
  activePanel.value = panel;
};

const onAddStep = (afterIndex, actionName, options = {}) => {
  emit('insertActionAfter', afterIndex, actionName, options);
};
</script>

<template>
  <div class="flex min-w-max shrink-0 flex-col items-center overflow-visible">
    <div class="relative">
      <WorkflowCanvasCard
        :title="$t('AUTOMATION.FLOW.NODE_TRIGGER')"
        :description="currentEventLabel"
        :icon="triggerVisual.hugeIcon"
        :icon-class="triggerVisual.iconClass"
        :is-active="triggerActive"
        :active-card-class="triggerVisual.cardBorderClass"
        :active-ring-class="triggerVisual.ringActiveClass"
        @click="select('trigger-conditions')"
      />
    </div>

    <WorkflowFlowConnector
      v-if="!rootStepId"
      v-model:active-add-menu-id="activeAddMenuId"
      connector-id="after-trigger"
      :line-class="triggerVisual.lineClass"
      :plus-button-class="triggerVisual.plusButtonClass"
      :items="addStepMenuItems"
      :is-read-only="isReadOnly"
      @select="actionName => onAddStep(-1, actionName)"
    />

    <div v-else class="flex flex-col items-center">
      <div
        class="h-[2.25rem] w-0.5"
        :class="triggerVisual.lineClass"
        aria-hidden="true"
      />
      <WorkflowStepTree
        :step-id="rootStepId"
        :actions="automation.actions"
        :automation-action-types="automationActionTypes"
        :add-step-menu-items="addStepMenuItems"
        :active-panel="activePanel"
        :active-add-menu-id="activeAddMenuId"
        :is-read-only="isReadOnly"
        :branch-target-ids="branchTargetIds"
        @update:active-panel="activePanel = $event"
        @update:active-add-menu-id="activeAddMenuId = $event"
        @insert-action-after="onAddStep"
      />
    </div>
  </div>
</template>
